// import dependencies
import React, { Component, Fragment } from 'react';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import kebabCase from 'lodash/kebabCase';
import { reaction } from 'mobx';
// import components
import OdometerPrice from '~/common/components/OdometerPrice';
import CommonButton from '~/common/components/CommonButton';
import Tooltip from '~/common/components/Tooltip';
import InfoIcon from '~/common/components/InfoIcon2';

import { scBeacon, createPersonalizedPurchaseEvent, clickCtaEvent, gtm, addToCartEvent, beginCheckoutFlowEvent } from '~/common/utils/tracking.js';
import { PLAN_TERM_ANNUAL, PLAN_TERM_MONTHLY, PATH_TRIAL_UPGRADE } from '~/common/utils/consts.js';

// import stylesheet
import styles from './plan-card.css';

@withRouter
@inject('offerSuiteStore', 'lockNumberStore', 'progressStore', 'convertToPaidStore', 'lockNumberStore', 'modalStore', 'trialSignupStore', 'promoCodeStore')
@observer
export default class extends Component {
    state = {
        discountsFetched: !!this.props.promoCodeStore.promoMaxAmount
    }

    autoDiscountsFetch = reaction(
        () => this.props.promoCodeStore.promoMaxAmount,
        () => {
            this.setState({ discountsFetched: true });
        }
    )

    isDefaultOffer() {
        const { selectedPlanTerm, defaultOffer } = this.props.offerSuiteStore;
        const { DescriptiveName } = this.props.planSet[selectedPlanTerm];
        return defaultOffer === DescriptiveName;
    }

    renderPlanDescription() {
        const { DescriptiveName } = this.props.planSet[this.props.offerSuiteStore.selectedPlanTerm];
        switch (DescriptiveName) {
            case 'True Solo':
                return 'Starter plan for the single person business.';
            case 'Solo Plus':
                return 'Add unlimited users to your account at no additional cost.';
            case 'Partner':
                return 'Great for growing teams.';
            case 'Small Business':
                return 'Best plan for flexibility and customization for small businesses.';
            default:
                return false;
        }
    }

    renderExtensionTooltipText() {
        const { DescriptiveName } = this.props.planSet[this.props.offerSuiteStore.selectedPlanTerm];
        switch (DescriptiveName) {
            case 'True Solo':
                return 'Enable call forwarding to a single number of your choosing with True Solo.';
            case 'Solo Plus':
                return 'Enable call forwarding to multiple phone numbers per extension - or they can all forward to you!';
            case 'Partner':
                return 'Extensions can forward to any phone number you choose - or they can all forward to you!';
            case 'Small Business':
                return 'Enable call forwarding to multiple phone numbers per extension - or they can all forward to you!';
            default:
                return false;
        }
    }

    selectOffer = (event) => {
        const { offerSuiteStore, lockNumberStore, planSet, progressStore, convertToPaidStore, trialSignupStore, modalStore } = this.props;
        if (trialSignupStore.isTrialFlow) {
            trialSignupStore.setIsTrialFlow(false);
        }

        // Show Modal for True Solo Trial Conversion
        if ((convertToPaidStore.isUserConvertingToPaid || convertToPaidStore.isTrialPassiveExpired) && planSet[offerSuiteStore.selectedPlanTerm].Name === 'True Solo' && (convertToPaidStore.trialUsersAndExtensions.users > 1 || convertToPaidStore.trialUsersAndExtensions.extensions > 2 || convertToPaidStore.trialUsersAndExtensions.callRecording)) {
            // Prevent redirection to Billing
            event.preventDefault();
            // Show Modal
            modalStore.setModalOptions({
                childComponentString: 'trueSoloTrialConversionModal',
                isModalClosable: false,
                modalType: 'large',
                isModalOpen: true,
                callback: () => {
                    // set selected offer ID in offerSuiteStore
                    offerSuiteStore.setSelectedOfferSet(planSet);
                    // Track Add to Cart
                    addToCartEvent(planSet);
                    gtm(clickCtaEvent('Proceed to checkout', convertToPaidStore.isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans, 'True Solo Trial conversion modal'));
                    gtm(beginCheckoutFlowEvent(1));

                    // track SCBeacon: saw Numbers page first and picked number
                    if (lockNumberStore.selectedNumberUI) scBeacon(createPersonalizedPurchaseEvent('1'));
                }
            });
        } else {
            // set selected offer ID in offerSuiteStore
            offerSuiteStore.setSelectedOfferSet(planSet);
            // Track Add to Cart
            addToCartEvent(planSet);
            gtm(clickCtaEvent('buy now', convertToPaidStore.isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans, 'pricing plan card'));
            gtm(beginCheckoutFlowEvent(1));

            // track SCBeacon: saw Numbers page first and picked number
            if (lockNumberStore.selectedNumberUI) scBeacon(createPersonalizedPurchaseEvent('1'));
        }
    };

    render() {
        const { offerSuiteStore, convertToPaidStore, progressStore, planSet, promoCodeStore } = this.props;
        const { selectedPlanTerm, isTermMonthly, isTermAnnual } = offerSuiteStore;
        const { IncludedUsers, IncludedExtensions, QuantityOfNumbersFree, DescriptiveName, Id } = planSet[selectedPlanTerm];
        const { discounts, isPercentPromo } = promoCodeStore;

        return (
            <div className={`${styles.wrapper}`}>
                <div className={`${styles.card} ${this.state.discountsFetched && discounts[Id] ? styles['card--no-padding'] : ''}  ${this.state.discountsFetched && discounts[Id] ? styles['card--highlight'] : ''}`}>

                    <div className={this.state.discountsFetched && discounts[Id] && styles['offer-banner']}>
                        {this.state.discountsFetched && discounts[Id] && <span className={styles['offer-banner-text']}>GET ADDITIONAL {isPercentPromo ? `${discounts[Id].discountPercent}%` : `$${discounts[Id].discount}`} OFF AT CHECKOUT</span>}
                    </div>

                    <div className={`${this.state.discountsFetched && discounts[Id] ? styles['card-padding-section'] : styles['card-section']}`}>
                        <h3 className={`${styles.header}`}>
                            {DescriptiveName}
                        </h3>

                        <p className={`${styles.description}`}>{this.renderPlanDescription()}</p>

                        <div className={`${styles['cost-info']}`}>

                            <Fragment>
                                <span className={`${styles['currency-sign']}`}>$</span>
                                <OdometerPrice
                                    currentPosition={isTermMonthly ? 0 : 1}
                                    fadedBorder={false}
                                    flowReverse={true}
                                    fromValue={planSet[PLAN_TERM_MONTHLY].monthlyPlanCost}
                                    modifier={`${styles.odometer}`}
                                    toValue={planSet[PLAN_TERM_ANNUAL].monthlyPlanCost}
                                />
                                <span className={`${styles['term-info']}`}>
                                    <span className={`${styles.monthly} ${styles['monthly-pricing']}`}>/mo</span>
                                </span>
                                <div className={isTermAnnual ? styles['annual-pricing'] : styles['visibilty-hidden']}>
                                            billed annually at ${planSet[PLAN_TERM_ANNUAL].monthlyPlanCost * 12}*
                                </div>
                            </Fragment>

                        </div>

                        <CommonButton
                            id={`plan-cta__${kebabCase(DescriptiveName).toLowerCase()}`}
                            modifier={`${styles.button}`}
                            onClick={this.selectOffer}
                            to={convertToPaidStore.isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans}
                        >
                        Buy Now
                        </CommonButton>

                        <div className={`${styles['included-detail']}`}>
                            {planSet[PLAN_TERM_MONTHLY].Name === 'True Solo' ?
                                <Fragment>
                                    <b>{IncludedUsers}</b> User{IncludedUsers !== 1 && 's'}
                                </Fragment>
                                :
                                <Tooltip
                                    content={<p>Add new users to your account anytime as your business grows – at no additional cost. You can set permissions for each user and grant or restrict access to certain features.</p>}
                                    contentModifiers={styles.tooltip}
                                >
                                    <b>{IncludedUsers}</b> User{IncludedUsers !== 1 && 's'} <InfoIcon modifier={`${styles['included-tooltip-icon']}`} />
                                </Tooltip>
                            }
                        </div>

                        <div className={`${styles['included-detail']}`}>
                            <Tooltip
                                content={
                                    <div>
                                        <p>You can select multiple numbers in different area codes, or select a combination of Toll Free and local!</p>
                                        <p>Call and send texts from any of your numbers.</p>
                                        <p>Pick one now, and add any additional numbers after checkout.</p>
                                    </div>
                                }
                                contentModifiers={styles.tooltip}
                            >
                                <b>{QuantityOfNumbersFree}</b> Phone Number{QuantityOfNumbersFree > 1 && 's*'} <InfoIcon modifier={`${styles['included-tooltip-icon']}`} />
                            </Tooltip>
                        </div>

                        <div className={`${styles['included-detail']}`}>
                            <Tooltip
                                content={<p>{this.renderExtensionTooltipText()}</p>}
                                contentModifiers={styles.tooltip}
                            >
                                <b>{IncludedExtensions}</b> Extension{IncludedExtensions !== 1 && 's'} <InfoIcon modifier={`${styles['included-tooltip-icon']}`} />
                            </Tooltip>
                        </div>
                        <p className={styles['info-text']}>*Pricing subject to sales tax.</p>
                    </div>
                </div>
            </div>
        );
    }
}

